import { useState, useMemo, useEffect } from 'react'
import { branch, renderComponent } from 'recompose'

import { withTranslate } from '@i18n/utils'

import LoginSignUpContainer from 'shared/components/LoginSignUpContainer'

import { CardSection } from '@components/CardSection'
import Button from '@components/Button'

import { deliveryProductsSelector, credentialTypeSelector } from '../../selectors'
import { userPendingSelector, userSelector } from 'auth/selectors'

import { DELIVERY_HOME, CREDENTIAL_DIGITAL, CREDENTIAL_PHYSICAL } from 'subscription-flow/constants'

import AccessOptionsContent from './AccessOptionsContent'

import purchaseStyles from 'subscription-flow/styles.module.scss'

const $AccessOptions = ({
    onNext,
    products: nonTranslatedProducts,
    value,
    credentialType,
    showDigitalOption,
    showPhysicalOption,
    translate: t,
}) => {
    const [selectedDeliveryRate, setSelectedDeliveryRate] = useState(value || null)
    const [isActiveAccessCard, setIsActiveAccessCard] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [enabledNextBtn, setEnableNextBtn] = useState(false)

    const requiredCredentialTypes = [CREDENTIAL_PHYSICAL, CREDENTIAL_DIGITAL]
    const deliveryHomeSelected = selectedDeliveryRate?.currentPrd?.prdctCd === DELIVERY_HOME
    const isRateDigitalOption = selectedDeliveryRate?.typeCode === CREDENTIAL_DIGITAL.toUpperCase()
    const products = useMemo(
        () => nonTranslatedProducts.map(prd => ({ ...prd, prdctName: t(prd.prdctNameKey) })),
        [nonTranslatedProducts]
    )

    useEffect(() => {
        !deliveryHomeSelected && setEnableNextBtn(false)
    }, [deliveryHomeSelected])

    const isRateSelected = rate => selectedDeliveryRate?.rateId === (rate.rateId || rate.name.toLowerCase())

    const containsRequiredStrings = credentialTypeArr =>
        requiredCredentialTypes.every(str => credentialTypeArr.includes(str))

    const shouldRenderProductsWithRates = credentialType.includes(CREDENTIAL_PHYSICAL)

    const accessCardsDesc = containsRequiredStrings(credentialType) ? (
        <div className="my-5">
            <span>
                <T>subscription.purchase.accessOptions.desc.part.one</T>
            </span>
            <span className="font-weight-bold">
                <T>subscription.purchase.accessOptions.desc.part.two</T>
            </span>
        </div>
    ) : (
        <div className="my-5">
            <T>subscription.purchase.accessOptions.desc</T>
        </div>
    )

    const selectDeliveryRate = rate => {
        const currentPrd = products.find(product => product.ratePlans.some(ratePlan => ratePlan.rateId === rate.rateId))

        if (selectedDeliveryRate?.rateId !== (rate.rateId || rate.name?.toLowerCase())) {
            setSelectedDeliveryRate({
                currentPrd,
                rateId: rate.rateId || rate.name?.toLowerCase(),
                rate,
            })
        }
    }

    const onNextClick = (data = {}) => {
        if (isRateDigitalOption) {
            onNext({ ...selectedDeliveryRate, displayValue: selectedDeliveryRate.prdctName }, data.delivery)
        } else {
            const product = products.find(prd => prd.prdctCd === selectedDeliveryRate.currentPrd.prdctCd)
            onNext(
                {
                    rateId: selectedDeliveryRate.rateId,
                    rate: selectedDeliveryRate.rate,
                    prdctCd: product.prdctCd,
                    displayValue:
                        selectedDeliveryRate.rate.name.length !== 0
                            ? selectedDeliveryRate.rate.name
                            : selectedDeliveryRate.currentPrd.prdctName,
                },
                data.delivery
            )
        }
    }

    return (
        <CardSection className={purchaseStyles.contentSection} loading={false}>
            <h3 className="text-uppercase font-weight-bold">
                <T>subscription.purchase.accessOptions</T>
            </h3>
            <AccessOptionsContent
                accessOptionProducts={products}
                isRateSelected={isRateSelected}
                accessCardsDesc={accessCardsDesc}
                showDigitalOption={showDigitalOption}
                setSelectedDeliveryRate={setSelectedDeliveryRate}
                setIsActiveAccessCard={setIsActiveAccessCard}
                isRateDigitalOption={isRateDigitalOption}
                showPhysicalOption={showPhysicalOption}
                showForm={showForm}
                setShowForm={setShowForm}
                selectDeliveryRate={selectDeliveryRate}
                selectedDeliveryRate={selectedDeliveryRate}
                isActiveAccessCard={isActiveAccessCard}
                onNextClick={onNextClick}
                deliveryHomeSelected={deliveryHomeSelected}
                setEnableNextBtn={setEnableNextBtn}
                shouldRenderProductsWithRates={shouldRenderProductsWithRates}
            />
            <div className="d-flex justify-content-end mt-5">
                <Button
                    btnStyle="primary"
                    onClick={onNextClick}
                    disabled={!selectedDeliveryRate || (showForm && !enabledNextBtn)}>
                    <T>subscription.purchase.label.next</T>
                </Button>
            </div>
        </CardSection>
    )
}

const AccessOptions = compose(
    connect(
        createStructuredSelector({
            user: userSelector,
            userPending: userPendingSelector,
            products: deliveryProductsSelector,
            credentialType: credentialTypeSelector,
        })
    ),
    withTranslate,
    branch(({ user }) => !user, renderComponent(LoginSignUpContainer), renderComponent($AccessOptions))
)(AccessOptions)

export default withTranslate(AccessOptions)
